'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import Annotation from 'react-codemirror/model/Annotation';
import { JSLINT } from 'DesignData/enums/settings/Categories';
import { DEMO_CODE, SETTINGS_ESLINTRC_ID, SETTINGS_ESLINT_DEMO_ID } from 'DesignData/constants/settings/ESLint';
import { HORIZONTAL } from 'DesignData/enums/AxisDirections';
import { JSON as TEMPLATE_FILE_TYPE_JSON, JS } from 'DesignData/constants/TemplateFileTypes';
import TemplateFileTypeToMode from 'DesignData/enums/TemplateFileTypeToMode';
import Small from 'UIComponents/elements/Small';
import UIButton from 'UIComponents/button/UIButton';
import UIToggle from 'UIComponents/input/UIToggle';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UILink from 'UIComponents/link/UILink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import IDECodeMirror from 'DesignEditorsUI/components/shared/IDECodeMirror';
import CodeAccordion from 'DesignComponents/inputs/codeAccordion/CodeAccordion';
import CodeAccordionItem from 'DesignComponents/inputs/codeAccordion/CodeAccordionItem';

const i18nOption = (optionName, field) => {
  return `designManager.settings.categories.${JSLINT}.fields.${optionName}.${field}`;
};

let sessionDemoCode = DEMO_CODE;
export default class JavaScriptLinter extends Component {
  constructor(...args) {
    super(...args);

    this.onDemoCodeChange = ({
      value
    }) => {
      sessionDemoCode = value;
    };
  }

  componentDidMount() {
    this.registerToolbarContent();
  }

  shouldComponentUpdate(nextProps) {
    const {
      props
    } = this;
    return props.isEnabled !== nextProps.isEnabled || props.isDefaultEslintConfig !== nextProps.isDefaultEslintConfig || props.eslintConfigAnnotations !== nextProps.eslintConfigAnnotations;
  }

  componentDidUpdate(prevProps) {
    const {
      props
    } = this;

    if (prevProps.isEnabled !== props.isEnabled || prevProps.isDefaultEslintConfig !== props.isDefaultEslintConfig) {
      this.registerToolbarContent();
    }
  }

  registerToolbarContent() {
    this.props.registerToolbarContent({
      leftContent: this.renderEslintrcResetButton(),
      middleContent: this.renderEslintToggle(),
      rightContent: this.renderEslintDocLink()
    });
  }

  renderI18nOptionMessage(optionName, field) {
    return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
      message: i18nOption(optionName, field)
    });
  }

  renderEslintrcResetButton() {
    return /*#__PURE__*/_jsx(UIButton, {
      size: "extra-small",
      onClick: this.props.reset,
      disabled: this.props.isDefaultEslintConfig,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `designManager.settings.categories.${JSLINT}.reset.title`
      })
    });
  }

  renderEslintDocLink() {
    return /*#__PURE__*/_jsx(Small, {
      children: /*#__PURE__*/_jsx(UILink, {
        href: "https://eslint.org/docs/user-guide/configuring",
        external: true,
        children: this.renderI18nOptionMessage('docLink', 'label')
      })
    });
  }

  renderEslintToggle() {
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(UIFormLabel, {
        children: /*#__PURE__*/_jsxs(Small, {
          children: [this.renderI18nOptionMessage('enableJsLint', 'label'), ' ', /*#__PURE__*/_jsx(UIHelpIcon, {
            size: "xxs",
            tooltipPlacement: "top",
            title: this.renderI18nOptionMessage('enableJsLint', 'help')
          })]
        })
      }), /*#__PURE__*/_jsx(UIToggle, {
        onChange: this.props.toggleJsLint,
        size: "extra-small",
        checked: this.props.isEnabled
      })]
    });
  }

  renderEslintrcEditor() {
    const fileType = TEMPLATE_FILE_TYPE_JSON;
    return /*#__PURE__*/_jsx(IDECodeMirror, {
      sessionId: SETTINGS_ESLINTRC_ID,
      codemirrorType: fileType,
      mode: TemplateFileTypeToMode[fileType],
      value: this.props.eslintConfig,
      onChange: this.props.onConfigChange,
      annotations: this.props.eslintConfigAnnotations,
      autofocus: true
    });
  }

  renderDemoCodeEditor() {
    const fileType = JS;
    return /*#__PURE__*/_jsx(IDECodeMirror, {
      sessionId: SETTINGS_ESLINT_DEMO_ID,
      codemirrorType: fileType,
      mode: TemplateFileTypeToMode[fileType],
      enableEsLint: true,
      onEsLint: this.props.setESLintDemoErrors,
      value: sessionDemoCode,
      onChange: this.onDemoCodeChange
    });
  }

  renderCodeAccordion() {
    return /*#__PURE__*/_jsxs(CodeAccordion, {
      direction: HORIZONTAL,
      id: "settings-jslint",
      children: [/*#__PURE__*/_jsx(CodeAccordionItem, {
        title: this.renderI18nOptionMessage('config', 'label'),
        codemirror: this.renderEslintrcEditor()
      }), /*#__PURE__*/_jsx(CodeAccordionItem, {
        title: this.renderI18nOptionMessage('demo', 'label'),
        codemirror: this.renderDemoCodeEditor()
      })]
    });
  }

  render() {
    return /*#__PURE__*/_jsx(UIFlex, {
      align: "start",
      justify: "start",
      direction: "row",
      className: "settings-editor settings-jslint-editor",
      children: /*#__PURE__*/_jsx(UIFlex, {
        align: "stretch",
        className: "settings-code-scroll-column",
        direction: "column",
        wrap: "wrap",
        children: this.renderCodeAccordion()
      })
    });
  }

}
JavaScriptLinter.propTypes = {
  isEnabled: PropTypes.bool.isRequired,
  eslintConfig: PropTypes.string.isRequired,
  eslintConfigAnnotations: PropTypes.arrayOf(PropTypes.instanceOf(Annotation)).isRequired,
  isDefaultEslintConfig: PropTypes.bool.isRequired,
  toggleJsLint: PropTypes.func.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  setESLintDemoErrors: PropTypes.func.isRequired,
  registerToolbarContent: PropTypes.func.isRequired
};